/**
 * Bounded self-heal for failed dynamic imports.
 *
 * A new deploy replaces chunk hashes, so a tab holding the old index.html gets
 * a 404 on its next lazy import. Reloading once picks up the fresh HTML and
 * fixes it — which is why three copies of a reload-on-failure guard existed.
 *
 * Each of them, though, could reload forever. They stored a TIMESTAMP and, on
 * the second failure inside the window, DELETED it — so the third failure was
 * treated as the first and reloaded again. That only stays harmless while a
 * reload actually fixes the import. On 2026-09-17 it did not: the prod WAF
 * answered `/assets/floating-ui.dom-<hash>.js` with a 403 (it blocks any URL
 * containing `.dom-`), so every visitor to the dashboard got a page that
 * reloaded about three times a second, forever, logged in or not.
 *
 * So: COUNT the attempts and never clear the count on failure. One reload, then
 * the error surfaces to the error boundary, which at least tells the user
 * something and leaves the page reachable. The count resets only when an import
 * actually succeeds, so a later genuine stale-deploy can still self-heal.
 */

const KEY = 'chunk-reload-attempts';
const MAX_ATTEMPTS = 1;

function read(): number {
  try {
    return parseInt(sessionStorage.getItem(KEY) || '0', 10) || 0;
  } catch {
    // Private mode / blocked storage: no memory means no bounded retry, so
    // never reload at all — an endless refresh is worse than one error screen.
    return MAX_ATTEMPTS;
  }
}

let guard: (() => boolean) | null = null;

/**
 * Let a long-running job veto the reload. Uploads register one: reloading
 * under a multi-GB upload throws the transfer away, which is far worse than
 * one route failing to load. A vetoed reload does not use up the attempt.
 */
export function setReloadGuard(fn: (() => boolean) | null): void {
  guard = fn;
}

/** True while something (uploads) must not be interrupted by a page load. */
export function reloadBlocked(): boolean {
  try { return !!guard?.(); } catch { return false; }
}

/** A failed lazy import — what a tab on an old build gets after a deploy. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as any)?.message || err || '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\d]+ failed|Unable to preload CSS/i.test(msg);
}

/**
 * Call when a dynamic import fails. Returns true if a reload was started (the
 * caller should stop doing anything else), false if the caller should surface
 * the error.
 */
export function reloadForFailedChunk(): boolean {
  try { if (guard?.()) return false; } catch { /* a broken guard never blocks self-heal */ }
  const attempts = read();
  if (attempts >= MAX_ATTEMPTS) return false;
  try { sessionStorage.setItem(KEY, String(attempts + 1)); } catch { return false; }
  window.location.reload();
  return true;
}

/** Call after a dynamic import succeeds, so a later stale deploy can self-heal. */
export function noteChunkLoadSuccess(): void {
  try {
    if (sessionStorage.getItem(KEY)) sessionStorage.removeItem(KEY);
  } catch { /* nothing to clear */ }
}

/**
 * Clear the attempt count because the USER asked to retry (the error screen's
 * Reload button). An explicit click is not a loop.
 */
export function resetChunkReloadAttempts(): void {
  noteChunkLoadSuccess();
}
